import type {
  FetchArgs,
  BaseQueryApi,
  QueryReturnValue,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from "@reduxjs/toolkit/query";

import {
  addNetworkLog,
  NetworkLog
} from "~/features/supportBundle/supportBundle.slice";
import { getStatusCodeFromError } from "~/lib/getStatusCodeFromError";

type NormalizedRequest = {
  method: string;
  url: string;
  body?: unknown;
};

/**
 * Normalize the `args` to a standard format due to RTK's type for `args`
 */
export const normalizeRequestArgs = (
  args: string | FetchArgs
): NormalizedRequest => {
  if (typeof args === "string") {
    return { method: "GET", url: args };
  }
  return {
    method: args.method || "GET",
    url: args.url,
    body: args.body as unknown
  };
};

/**
 * Logs all requests that go through RTK Query.
 */
export const logNetworkRequest = (
  args: string | FetchArgs,
  api: BaseQueryApi,
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
  startTime: number,
  endTime: number
) => {
  const duration = endTime - startTime;
  const { method, url, body } = normalizeRequestArgs(args);

  // Don't log support bundle requests because of their large payloads
  const isSupportBundleRequest = url.includes("support-bundle");
  if (isSupportBundleRequest) {
    return;
  }

  const responseStatusCode = result.error
    ? getStatusCodeFromError(result.error)
    : result.meta?.response?.status;

  const networkLog: NetworkLog = {
    timestamp: new Date().toString(),
    duration,
    error: result.error ? JSON.stringify(result.error) : undefined,
    request: {
      startTime,
      method,
      url,
      body
    },
    response: {
      statusCode: responseStatusCode,
      body: result.data
    }
  };

  api.dispatch(addNetworkLog(networkLog));
};
